import * as Sentry from '@sentry/nextjs';
import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';

import type { Getter, Setter } from 'jotai';

import {
  audioInitializedAtom,
  audioPlayerStateAtom,
  pendingSeekPositionAtom,
  playbackAtom,
} from '@/features/AudioPlayer/atoms/core/primaryAtoms';
import {
  isPlayingAtom,
  playbackControlAtom,
  seekPositionAtom,
} from '@/features/AudioPlayer/atoms/playback/playbackAtoms';
import {
  activateQueueAtom,
  findTrackInLoadedQueuesAtom,
  queueAtom,
  setCurrentQueueFromPageAtom,
} from '@/features/AudioPlayer/atoms/queue/queueAtoms';
import { log } from '@/lib/utils';

import type { AudioTrack, QueueState } from '@/features/AudioPlayer/types';
import type { Atom } from 'jotai';

/**
 * TRACK-RELATED ATOMS
 * These atoms provide information about the currently playing track
 * and operations specific to tracks
 */

/**
 * Get the current track from the queue based on currentIndex
 * Returns null if no track is selected or the queue is empty
 */
export const currentTrackAtom = atom<AudioTrack | null>((get) => {
  const queue = get(queueAtom);

  if (queue.currentIndex < 0 || queue.trackOrder.length === 0) {
    return null;
  }

  const currentTrackId = queue.trackOrder[queue.currentIndex];

  return queue.tracks.get(currentTrackId) ?? null;
});
currentTrackAtom.debugLabel = 'currentTrackAtom';

/**
 * Whether a track is currently selected. Boolean-only so subscribers re-render
 * when the player goes from "no track" to "a track", not on every track change.
 * Used to mount the lazily-loaded player on first play intent (DEV-6487).
 */
export const hasCurrentTrackAtom = atom<boolean>(
  (get) => get(currentTrackAtom) !== null
);
hasCurrentTrackAtom.debugLabel = 'hasCurrentTrackAtom';

/**
 * Get the current track ID from the queue
 * Returns null if no track is selected or the queue is empty
 */
export const currentTrackIdAtom = atom<number | null>((get) => {
  const queue = get(queueAtom);

  if (queue.currentIndex < 0 || queue.trackOrder.length === 0) {
    return null;
  }

  return queue.trackOrder[queue.currentIndex] ?? null;
});
currentTrackIdAtom.debugLabel = 'currentTrackIdAtom';

/**
 * Get track-specific progress - more efficient for components to use this
 * than calculate it themselves, and ensures consistency
 */
export const getTrackProgressAtom = (trackId: number) =>
  atom((get) => {
    const currentTrackId = get(currentTrackIdAtom);

    const { currentTime, duration } = get(playbackAtom);

    // Only return progress for the current track, whether playing or paused
    // We only reset to 0 if it's not the current track or has no duration
    if (currentTrackId !== trackId || duration <= 0) {
      return 0;
    }

    // Calculate and return the normalized progress (0-1)
    return Math.min(currentTime / duration, 1);
  });
getTrackProgressAtom.debugLabel = 'getTrackProgressAtom';

// Memoize the track lookup to avoid repeated Map.get operations
const getTrackFromQueue = (
  queue: QueueState,
  trackId: number
): { track: AudioTrack | null; index: number } => {
  if (!queue.tracks.has(trackId)) {
    return { track: null, index: -1 };
  }

  const index = queue.trackOrder.indexOf(trackId);

  return { track: queue.tracks.get(trackId) ?? null, index };
};

// Helper function to handle playing a track from the current queue
const playTrackFromCurrentQueue = (
  get: Getter,
  set: Setter,
  queue: QueueState,
  trackId: number,
  currentQueueIndex: number,
  currentTrackId: number | null
) => {
  if (currentTrackId !== trackId) {
    const updates = {
      currentIndex: currentQueueIndex,
      history:
        queue.currentIndex >= 0
          ? [...queue.history, queue.currentIndex]
          : queue.history,
    };

    set(queueAtom, {
      ...queue,
      ...updates,
    });
  }

  set(playbackControlAtom, 'play');
  set(audioInitializedAtom, true);
};

// Helper function to handle priming a track from the current queue
const primeTrackFromCurrentQueue = (
  get: Getter,
  set: Setter,
  queue: QueueState,
  trackId: number,
  currentQueueIndex: number,
  currentTrackId: number | null
) => {
  if (currentTrackId !== trackId) {
    const updates = {
      currentIndex: currentQueueIndex,
      history:
        queue.currentIndex >= 0
          ? [...queue.history, queue.currentIndex]
          : queue.history,
    };

    set(queueAtom, {
      ...queue,
      ...updates,
    });
  }
  set(audioInitializedAtom, true);
};

/**
 * Play a track by ID using intelligent queue selection
 *
 * Logic flow:
 * 1. If track exists in BOTH current and loaded queues → use timestamp to pick newest
 * 2. If track exists ONLY in loaded queue → activate that queue
 * 3. If track exists ONLY in current queue → play from current queue
 * 4. If track not found → do nothing
 *
 * This enables filtered contexts to override older playing contexts while
 * maintaining backward compatibility for tracks that only exist in one queue.
 */
export const playTrackByIdAtom = atom(null, (get, set, trackId: number) => {
  log('playTrackByIdAtom called with trackId:', trackId);
  Sentry.addBreadcrumb({
    category: 'audio_player',
    message: `Playing track by ID: ${trackId}`,
    level: 'info',
  });

  const playerState = get(audioPlayerStateAtom);
  const currentTrackId = get(currentTrackIdAtom);

  // Check both current queue and loaded queues to determine which has the track
  const queue = playerState.currentQueue;
  const { track: currentQueueTrack, index: currentQueueIndex } =
    getTrackFromQueue(queue, trackId);

  const findTrackInLoadedQueues = get(findTrackInLoadedQueuesAtom);
  const loadedTrackInfo = findTrackInLoadedQueues(trackId);

  // If track exists in both queues, compare timestamps to use the most recent one
  if (currentQueueTrack && loadedTrackInfo) {
    const loadedQueue = playerState.loadedQueues.find(
      (q) => q.id === loadedTrackInfo.queueId
    );

    if (!loadedQueue) {
      log(
        'ERROR: Loaded queue not found for queueId:',
        loadedTrackInfo.queueId
      );
      return;
    }

    // Use timestamp-based priority: loaded queues with newer timestamps take precedence
    // This ensures filtered contexts override older playing contexts
    const currentTimestamp = queue.timestamp || 0;
    const loadedTimestamp = loadedQueue.timestamp || 0;
    const shouldUseLoadedQueue =
      loadedTimestamp > currentTimestamp && loadedTimestamp > 0;

    if (shouldUseLoadedQueue) {
      // Use the newer loaded queue
      set(activateQueueAtom, {
        queueId: loadedTrackInfo.queueId,
        startIndex: loadedTrackInfo.trackIndex,
      });
      set(playbackControlAtom, 'play');
      set(audioInitializedAtom, true);

      return;
    }

    // Use current queue (it's newer or same age)
    playTrackFromCurrentQueue(
      get,
      set,
      queue,
      trackId,
      currentQueueIndex,
      currentTrackId
    );

    return;
  }

  // Track found only in loaded queue - activate it
  if (loadedTrackInfo) {
    Sentry.addBreadcrumb({
      category: 'audio_player',
      message: `Track ${trackId} found in loaded queue ${loadedTrackInfo.queueId}`,
      level: 'info',
    });

    set(activateQueueAtom, {
      queueId: loadedTrackInfo.queueId,
      startIndex: loadedTrackInfo.trackIndex,
    });
    set(playbackControlAtom, 'play');
    set(audioInitializedAtom, true);

    return;
  }

  // Track found only in current queue - play it
  if (currentQueueTrack) {
    Sentry.addBreadcrumb({
      category: 'audio_player',
      message: `Track ${trackId} found in current queue at index ${currentQueueIndex}`,
      level: 'info',
    });

    playTrackFromCurrentQueue(
      get,
      set,
      queue,
      trackId,
      currentQueueIndex,
      currentTrackId
    );
    return;
  }

  // If the track is not found in any queue, log warning but don't capture to Sentry
  // This is an expected condition that can happen when navigating between pages
  console.warn(`Track with ID ${trackId} not found in any queue`);
});
playTrackByIdAtom.debugLabel = 'playTrackByIdAtom';

/**
 * Primes a track by ID using intelligent queue selection
 *
 * Logic flow:
 * 1. If track exists in BOTH current and loaded queues → use timestamp to pick newest
 * 2. If track exists ONLY in loaded queue → activate that queue
 * 3. If track exists ONLY in current queue → prime from current queue
 * 4. If track not found → do nothing
 *
 * This enables filtered contexts to override older playing contexts while
 * maintaining backward compatibility for tracks that only exist in one queue.
 */
export const primeTrackByIdAtom = atom(null, (get, set, trackId: number) => {
  log('primeTrackByIdAtom called with trackId:', trackId);
  Sentry.addBreadcrumb({
    category: 'audio_player',
    message: `Playing track by ID: ${trackId}`,
    level: 'info',
  });

  const playerState = get(audioPlayerStateAtom);
  const currentTrackId = get(currentTrackIdAtom);

  // Check both current queue and loaded queues to determine which has the track
  const queue = playerState.currentQueue;
  const { track: currentQueueTrack, index: currentQueueIndex } =
    getTrackFromQueue(queue, trackId);

  const findTrackInLoadedQueues = get(findTrackInLoadedQueuesAtom);
  const loadedTrackInfo = findTrackInLoadedQueues(trackId);

  // If track exists in both queues, compare timestamps to use the most recent one
  if (currentQueueTrack && loadedTrackInfo) {
    const loadedQueue = playerState.loadedQueues.find(
      (q) => q.id === loadedTrackInfo.queueId
    );

    if (!loadedQueue) {
      log(
        'ERROR: Loaded queue not found for queueId:',
        loadedTrackInfo.queueId
      );
      return;
    }

    // Use timestamp-based priority: loaded queues with newer timestamps take precedence
    // This ensures filtered contexts override older playing contexts
    const currentTimestamp = queue.timestamp || 0;
    const loadedTimestamp = loadedQueue.timestamp || 0;
    const shouldUseLoadedQueue =
      loadedTimestamp > currentTimestamp && loadedTimestamp > 0;

    if (shouldUseLoadedQueue) {
      // Use the newer loaded queue
      set(activateQueueAtom, {
        queueId: loadedTrackInfo.queueId,
        startIndex: loadedTrackInfo.trackIndex,
      });
      set(audioInitializedAtom, true);

      return;
    }

    // Use current queue (it's newer or same age)
    primeTrackFromCurrentQueue(
      get,
      set,
      queue,
      trackId,
      currentQueueIndex,
      currentTrackId
    );

    return;
  }

  // Track found only in loaded queue - activate it
  if (loadedTrackInfo) {
    Sentry.addBreadcrumb({
      category: 'audio_player',
      message: `Track ${trackId} found in loaded queue ${loadedTrackInfo.queueId}`,
      level: 'info',
    });

    set(activateQueueAtom, {
      queueId: loadedTrackInfo.queueId,
      startIndex: loadedTrackInfo.trackIndex,
    });
    set(audioInitializedAtom, true);

    return;
  }

  // Track found only in current queue - play it
  if (currentQueueTrack) {
    Sentry.addBreadcrumb({
      category: 'audio_player',
      message: `Track ${trackId} found in current queue at index ${currentQueueIndex}`,
      level: 'info',
    });

    primeTrackFromCurrentQueue(
      get,
      set,
      queue,
      trackId,
      currentQueueIndex,
      currentTrackId
    );
    return;
  }

  // If the track is not found in any queue, log warning but don't capture to Sentry
  // This is an expected condition that can happen when navigating between pages
  console.warn(`Track with ID ${trackId} not found in any queue`);
});
primeTrackByIdAtom.debugLabel = 'primeTrackByIdAtom';

/**
 * Play a track by ID and seek to a specific percentage position
 * This is used when clicking on a waveform to play from that position
 */
export const playTrackAtPositionAtom = atom(
  null,
  (get, set, payload: { trackId: number; percentage: number }) => {
    const { trackId, percentage } = payload;

    log('playTrackAtPositionAtom called with:', { trackId, percentage });
    Sentry.addBreadcrumb({
      category: 'audio_player',
      message: `Playing track ${trackId} at position ${(percentage * 100).toFixed(1)}%`,
      level: 'info',
    });

    const currentTrackId = get(currentTrackIdAtom);

    // If this is already the current track
    if (currentTrackId === trackId) {
      // Check if we have a valid duration for this track
      const trackDurationAtom = trackDurationAtomFamily(trackId);
      const trackDuration = get(trackDurationAtom);

      // If duration is ready, seek directly
      if (trackDuration > 0) {
        set(seekPositionAtom, { trackId, percentage });
        log('Seeking current track with known duration:', {
          trackId,
          percentage,
          trackDuration,
        });
      } else {
        // Duration not ready yet - store as pending seek position
        // This handles the case where the track is current but hasn't loaded yet
        set(pendingSeekPositionAtom, { trackId, percentage });
        log('Stored pending seek for current track (duration not ready):', {
          trackId,
          percentage,
        });
        Sentry.addBreadcrumb({
          category: 'audio_player',
          message: `Pending seek for current track ${trackId} (duration not ready)`,
          level: 'info',
        });
      }
      return;
    }

    // For a different track, store the pending seek position
    // This will be applied immediately when the track loads, preventing
    // the "play then jump" glitch
    set(pendingSeekPositionAtom, { trackId, percentage });

    // Start playing the track
    set(playTrackByIdAtom, trackId);
  }
);
playTrackAtPositionAtom.debugLabel = 'playTrackAtPositionAtom';

/**
 * Combined utility to find and play a track
 * Returns true if the track was found and playback started
 */
export const findTrackAtom = atom(
  null,
  (get, set, trackId: number): boolean => {
    const { currentQueue } = get(audioPlayerStateAtom);

    // Check if the track is already in the current queue - use Map.has for O(1) check
    const { track, index: currentQueueIndex } = getTrackFromQueue(
      currentQueue,
      trackId
    );

    if (track) {
      // If found in current queue, update the current index and play
      // Batch state updates
      const updates = {
        currentIndex: currentQueueIndex,
        history:
          currentQueue.currentIndex >= 0
            ? [...currentQueue.history, currentQueue.currentIndex]
            : currentQueue.history,
      };

      set(queueAtom, {
        ...currentQueue,
        ...updates,
      });

      set(playbackControlAtom, 'play');

      return true;
    }

    // If not in current queue, use the finder function
    const findTrackInLoadedQueues = get(findTrackInLoadedQueuesAtom);
    const loadedTrackInfo = findTrackInLoadedQueues(trackId);

    if (loadedTrackInfo) {
      // Activate the queue that contains this track and start playback from this track
      const { queueId, trackIndex } = loadedTrackInfo;

      set(activateQueueAtom, {
        queueId,
        startIndex: trackIndex,
      });
      set(playbackControlAtom, 'play');

      return true;
    }

    return false;
  }
);
findTrackAtom.debugLabel = 'findTrackAtom';

// Cache for track state objects to maintain referential equality (now unused but kept for potential future use)
const _trackStateCache = new Map<
  string,
  { isPlaying: boolean; isCurrent: boolean }
>();

// Create base states that won't cause re-renders
const FALSE_PLAYING_STATE = { isPlaying: false, isCurrent: false };
const FALSE_CURRENT_STATE = { isPlaying: false, isCurrent: true };
const TRUE_PLAYING_STATE = { isPlaying: true, isCurrent: true };

/**
 * Family of atoms to track when seek operations complete for each track
 * Used to clear optimistic UI state after seek is done
 * Each track gets its own completion timestamp atom
 */
export const trackSeekCompletionAtomFamily = atomFamily((trackId: number) => {
  const completionAtom = atom<number>(0);
  completionAtom.debugLabel = `trackSeekCompletion_${trackId}`;
  return completionAtom;
});

// Set up automatic cleanup for seek completion atoms
trackSeekCompletionAtomFamily.setShouldRemove((createdAt, _param) => {
  const CLEANUP_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes
  const shouldRemove = Date.now() - createdAt > CLEANUP_THRESHOLD_MS;
  return shouldRemove;
});

/**
 * Family of atoms for individual track durations
 *
 * This prevents race conditions where position calculations use the wrong track's duration.
 * Each track gets its own duration atom that's updated when that specific track loads,
 * ensuring waveform position calculations always use the correct track's duration.
 *
 * Usage: trackDurationAtomFamily(123) creates an atom that stores the duration for track ID 123
 */
export const trackDurationAtomFamily = atomFamily((trackId: number) => {
  const durationAtom = atom<number>(0);
  durationAtom.debugLabel = `trackDuration_${trackId}`;
  return durationAtom;
});

// Set up automatic cleanup for duration atoms to prevent memory leaks
trackDurationAtomFamily.setShouldRemove((createdAt, _param) => {
  const CLEANUP_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes
  const shouldRemove = Date.now() - createdAt > CLEANUP_THRESHOLD_MS;
  return shouldRemove;
});

/**
 * Family of atoms for individual track playing states
 *
 * Performance optimization: Instead of having all Track components subscribe to global
 * currentTrackIdAtom and isPlayingAtom (which causes all tracks to re-render when any
 * track plays/pauses), this creates individual atoms for each track.
 *
 * Each track gets its own derived atom that only changes when that specific track's
 * playing state changes, preventing unnecessary re-renders of other tracks.
 *
 * Memory management: Atoms are automatically cleaned up after 5 minutes of inactivity
 * to prevent memory leaks when users navigate through many track listings.
 *
 * Usage: trackPlayingStateAtomFamily(123) creates an atom that returns an object with:
 * - isPlaying: true when track ID 123 is currently playing
 * - isCurrent: true when track ID 123 is the current track (playing or paused)
 */
export const trackPlayingStateAtomFamily = atomFamily((trackId: number) => {
  // Store the last result to enable early returns and prevent unnecessary re-renders
  let lastResult: { isPlaying: boolean; isCurrent: boolean } =
    FALSE_PLAYING_STATE;

  const trackAtom = atom((get) => {
    const currentId = get(currentTrackIdAtom);
    const playing = get(isPlayingAtom);

    // Determine the new state for this specific track
    const isCurrent = currentId === trackId;
    const isPlaying = isCurrent && playing;

    // If this track's state hasn't changed, return the exact same object to prevent re-render
    if (
      lastResult.isCurrent === isCurrent &&
      lastResult.isPlaying === isPlaying
    ) {
      return lastResult;
    }

    // State has changed for this track, determine which pre-defined state object to return
    let newResult: { isPlaying: boolean; isCurrent: boolean };

    if (isPlaying && isCurrent) {
      newResult = TRUE_PLAYING_STATE;
    } else if (isCurrent && !isPlaying) {
      newResult = FALSE_CURRENT_STATE;
    } else {
      newResult = FALSE_PLAYING_STATE;
    }

    lastResult = newResult;

    return newResult;
  });
  trackAtom.debugLabel = `trackPlayingState_${trackId}`;
  return trackAtom;
});

// Set up automatic cleanup for unused atoms to prevent memory leaks
// Remove atoms that haven't been accessed in the last 5 minutes
trackPlayingStateAtomFamily.setShouldRemove((createdAt, _param) => {
  const CLEANUP_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes
  const shouldRemove = Date.now() - createdAt > CLEANUP_THRESHOLD_MS;

  return shouldRemove;
});

/**
 * Play a track from a page queue atom
 * This is the new simplified approach for search pages that use derived queue atoms
 */
export const playTrackFromPageQueueAtom = atom(
  null,
  (
    get,
    set,
    {
      trackId,
      pageQueueAtom,
    }: { trackId: number; pageQueueAtom: Atom<QueueState | null> }
  ) => {
    const pageQueueState = get(pageQueueAtom);

    if (!pageQueueState) {
      log('Page queue state is null, cannot play track', { trackId });
      return;
    }

    // Find the track in the page queue
    const trackIndex = pageQueueState.trackOrder.indexOf(trackId);
    if (trackIndex < 0) {
      console.error('Track not found in page queue - detailed debug:', {
        requestedTrackId: trackId,
        trackOrderArray: pageQueueState.trackOrder,
        trackOrderFirst10: pageQueueState.trackOrder.slice(0, 10),
        tracksMapKeys: Array.from(pageQueueState.tracks.keys()).slice(0, 10),
        totalTracksInOrder: pageQueueState.trackOrder.length,
        totalTracksInMap: pageQueueState.tracks.size,
        context: pageQueueState.context.source,
      });
      log('Track not found in page queue', {
        trackId,
        trackOrder: pageQueueState.trackOrder,
        tracksInMap: Array.from(pageQueueState.tracks.keys()),
        context: pageQueueState.context,
      });
      console.warn(`Track ${trackId} not in queue - play cancelled`);
      return;
    }

    // Set current queue from page queue with the correct starting index
    const queueStateWithIndex = {
      ...pageQueueState,
      currentIndex: trackIndex,
    };

    set(setCurrentQueueFromPageAtom, queueStateWithIndex);
    set(playbackControlAtom, 'play');
    set(audioInitializedAtom, true);

    log('Playing track from page queue', {
      trackId,
      trackIndex,
      totalTracks: pageQueueState.tracks.size,
      context: pageQueueState.context.source,
    });
  }
);
playTrackFromPageQueueAtom.debugLabel = 'playTrackFromPageQueueAtom';

/**
 * Play a track from a page queue atom
 * This is the new simplified approach for search pages that use derived queue atoms
 */
export const primeTrackFromPageQueueAtom = atom(
  null,
  (
    get,
    set,
    {
      trackId,
      pageQueueAtom,
    }: { trackId: number; pageQueueAtom: Atom<QueueState | null> }
  ) => {
    const pageQueueState = get(pageQueueAtom);

    if (!pageQueueState) {
      log('Page queue state is null, cannot play track', { trackId });
      return;
    }

    // Find the track in the page queue
    const trackIndex = pageQueueState.trackOrder.indexOf(trackId);
    if (trackIndex < 0) {
      console.error('Track not found in page queue - detailed debug:', {
        requestedTrackId: trackId,
        trackOrderArray: pageQueueState.trackOrder,
        trackOrderFirst10: pageQueueState.trackOrder.slice(0, 10),
        tracksMapKeys: Array.from(pageQueueState.tracks.keys()).slice(0, 10),
        totalTracksInOrder: pageQueueState.trackOrder.length,
        totalTracksInMap: pageQueueState.tracks.size,
        context: pageQueueState.context.source,
      });
      log('Track not found in page queue', {
        trackId,
        trackOrder: pageQueueState.trackOrder,
        tracksInMap: Array.from(pageQueueState.tracks.keys()),
        context: pageQueueState.context,
      });
      console.warn(`Track ${trackId} not in queue - play cancelled`);
      return;
    }

    // Set current queue from page queue with the correct starting index
    const queueStateWithIndex = {
      ...pageQueueState,
      currentIndex: trackIndex,
    };

    set(setCurrentQueueFromPageAtom, queueStateWithIndex);
    set(audioInitializedAtom, true);

    log('Playing track from page queue', {
      trackId,
      trackIndex,
      totalTracks: pageQueueState.tracks.size,
      context: pageQueueState.context.source,
    });
  }
);
primeTrackFromPageQueueAtom.debugLabel = 'primeTrackFromPageQueueAtom';
